import { api } from './api';

export const INTERACTION_SEVERITY_META = {
  minor: { label: 'Minor', bg: 'secondary' },
  moderate: { label: 'Moderate', bg: 'warning' },
  major: { label: 'Major', bg: 'danger' },
  severe: { label: 'Severe', bg: 'dark' },
};

export const normaliseDrugName = (value) => String(value || '').trim().toLowerCase();

const resolveDrugIdByName = (name, drugLibrary) => {
  if (!name || !Array.isArray(drugLibrary)) return null;
  const needle = normaliseDrugName(name);
  if (!needle) return null;
  const match = drugLibrary.find((d) => normaliseDrugName(d?.drugName) === needle);
  return match?.id ? String(match.id) : null;
};

// Find duplicate warnings within a set of entries. Returns one warning per
// distinct drug name that appears more than once (across active entries).
//
// Entries shape: [{ id?, name, status? }]
// Excludes entries where status === 'stopped'.
export const findDuplicateWarnings = (entries = []) => {
  const groups = new Map();
  entries.forEach((entry) => {
    if (!entry || entry.status === 'stopped') return;
    const name = normaliseDrugName(entry.name);
    if (!name) return;
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(entry);
  });
  const warnings = [];
  groups.forEach((group, name) => {
    if (group.length < 2) return;
    const displayName = group[0].name || name;
    warnings.push({
      id: `duplicate-${name}`,
      kind: 'duplicate',
      severity: 'major',
      drugAName: displayName,
      drugBName: displayName,
      note: `${displayName} appears ${group.length} times. Confirm the duplicate is intentional (e.g. dose change, route change) before continuing.`,
    });
  });
  return warnings;
};

// Fetch pair-interaction warnings for a list of drug IDs. Returns empty array
// on failure or when fewer than two IDs are provided.
export const fetchPairWarnings = async (drugIds, token) => {
  const uniqueIds = Array.from(new Set((drugIds || []).filter(Boolean).map(String)));
  if (uniqueIds.length < 2 || !token) return [];
  try {
    const result = await api.checkDrugInteractions(uniqueIds, token);
    return Array.isArray(result?.interactions) ? result.interactions : [];
  } catch (err) {
    return [];
  }
};

// One-call helper: given entries (each with name + optional drugId), resolve
// drug IDs (using the library if needed) and return combined duplicate +
// pair warnings.
//
// Entries: [{ id?, name, drugId?, status? }]
// drugLibrary: optional drug-library items array (used to resolve missing
//   drugIds by name).
export const runFullInteractionCheck = async ({ entries = [], token, drugLibrary }) => {
  const duplicates = findDuplicateWarnings(entries);
  const resolvedIds = entries
    .filter((e) => e && e.status !== 'stopped')
    .map((e) => {
      if (e.drugId && e.drugId !== 'freeform') return String(e.drugId);
      return resolveDrugIdByName(e.name, drugLibrary);
    })
    .filter(Boolean);
  const pairs = await fetchPairWarnings(resolvedIds, token);
  return [...duplicates, ...pairs];
};
